import type { Editor } from "@tiptap/react";

import type { CollaborateUserProfile, UserId } from "../types/collaborateThread";
import { resolveUserRefById, stripSignoffTags } from "./parseSignoff";

const MENTION_TAG_RE = /<mention(?:\s+id="([^"]*)")?\s*>@?([^<]*)<\/mention>/gi;

/** Extract user IDs from serialized `<mention id="…">` tags (id required; plain `@Name` is ignored). */
export function parseMentionIdsFromBody(
  text: string,
  users: CollaborateUserProfile[],
): UserId[] {
  const ids = new Set<UserId>();
  const stripped = stripSignoffTags(text);

  MENTION_TAG_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = MENTION_TAG_RE.exec(stripped)) !== null) {
    const id = match[1]?.trim();
    if (!id) {
      continue;
    }
    const byId = resolveUserRefById(id, users);
    if (byId) {
      ids.add(byId.id);
    } else {
      ids.add(id);
    }
  }
  return Array.from(ids);
}

/** Read @mention user IDs from TipTap mention nodes (picker selections with user id only). */
export function getMentionIdsFromEditor(
  editor: Editor,
  _users: CollaborateUserProfile[],
): UserId[] {
  const ids = new Set<UserId>();
  editor.state.doc.descendants((node) => {
    if (node.type.name !== "mention") {
      return;
    }
    const id = String(node.attrs.id ?? "").trim();
    if (id) {
      ids.add(id);
    }
  });
  return Array.from(ids);
}

/** @deprecated Prefer {@link getMentionIdsFromEditor} when an editor is available. */
export function parseMentionIds(text: string, users: CollaborateUserProfile[]): UserId[] {
  return parseMentionIdsFromBody(text, users);
}
